import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlus, ShieldCheck } from "lucide-react";

const ROLE_LABEL = { owner: "Owner / Superadmin", admin: "Admin", editor: "Redakteur", analyst: "Analyst", reviewer: "Reviewer" };
const ROLE_DESC = {
  owner: "Alles sehen & ändern, Nutzer & API-Verbindungen verwalten.",
  admin: "Projekte, Accounts, Imports, Posts & Reports verwalten.",
  editor: "Assets, Captions & Posts bearbeiten.",
  analyst: "Nur Analysen, Kommentare, Reports.",
  reviewer: "Posts freigeben, keine Systemeinstellungen.",
};

export default function Nutzer() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("Nutzer eingeladen"); setOpen(false); load(); setForm({ name: "", email: "", password: "", role: "editor" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const patch = async (id, body) => { try { await api.patch(`/users/${id}`, body); load(); toast.success("Aktualisiert"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader overline="Verwaltung" title="Nutzer & Rollen" subtitle="Interne Nutzer einladen, Rollen ändern & deaktivieren. Rechte gelten mandantenübergreifend."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="user-add-btn" className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><UserPlus size={15} className="mr-1.5" /> Nutzer einladen</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Nutzer einladen</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Name</Label><Input data-testid="user-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="text-xs">E-Mail</Label><Input data-testid="user-email" type="email" className="mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="text-xs">Passwort</Label><Input data-testid="user-password" type="text" className="mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                <div><Label className="text-xs">Rolle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABEL).filter(([k]) => k !== "owner").map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                  <p className="text-xs text-slate-400 mt-1">{ROLE_DESC[form.role]}</p>
                </div>
              </div>
              <DialogFooter><Button data-testid="user-create-btn" onClick={create} className="rounded-full bg-[#0A0A0A]">Einladen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Card className="rounded-md border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="px-4 py-2 font-semibold">Nutzer</th><th className="px-4 py-2 font-semibold">Rolle</th><th className="px-4 py-2 font-semibold">Login</th><th className="px-4 py-2 font-semibold">Aktiv</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-xs font-semibold">{u.name?.slice(0, 1).toUpperCase()}</div>
                    <div><div className="font-semibold">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {u.role === "owner" ? <span className="flex items-center gap-1 text-sm font-medium"><ShieldCheck size={14} className="text-emerald-600" /> {ROLE_LABEL.owner}</span> : (
                    <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}>
                      <SelectTrigger data-testid={`user-role-${u.id}`} className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(ROLE_LABEL).filter(([k]) => k !== "owner").map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">{u.auth_provider}</td>
                <td className="px-4 py-2.5">
                  {u.role !== "owner" ? <Switch data-testid={`user-active-${u.id}`} checked={u.active} onCheckedChange={(v) => patch(u.id, { active: v })} /> : <span className="text-xs text-slate-400">immer</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
